/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { useCheckoutSend, useCheckoutStateSelector } from "@checkout/context";
import type { FiatPrice } from "@hopper-b2b/types";
import {
  getIsRewardsSelected,
  getRewardsAmountToApply,
  getRewardsAmountToApplyInCash,
  setRewardsAmountToPay,
  setRewardsAmountToPayInCash,
  useAccountPoints,
  useConvertPointsToCashQuery,
  type RewardsPaymentEvents,
  type UseAccountPointsReturn,
} from "@hsbc/rewards";
import { ZERO_FIAT_AMOUNT } from "@hsbc/shared";

/**
 * Parse point in local string, e.g. 123,456 to 123456
 */
const parsePoint = (points: string | number) => {
  if (typeof points === "string") {
    return Number.parseInt(points.replace(/[^\d]/g, ""), 10) || 0;
  } else {
    return points;
  }
};

interface IUseRewardsSection {
  context: Omit<
    UseAccountPointsReturn,
    "isToPointLoading" | "isBalanceLoading"
  > & {
    rewardsSelected: boolean;
    rewardsPayInPoints: number;
    rewardsPayInCash: FiatPrice;
    maxToPayInCash: FiatPrice;
    isLoading: boolean;
  };
  handlers: {
    onChange: (points: string | number) => void;
    onValueCommit: (points: string | number) => void;
  };
}

export const useRewardsSection = (): IUseRewardsSection => {
  const [rewardsPayInPoints, setRewardsPayInPoints] = useState(0);

  const send = useCheckoutSend<RewardsPaymentEvents>();

  const isRewardsSelected = useCheckoutStateSelector(getIsRewardsSelected);
  const {
    accountPoints,
    rewardsPayInCashData,
    rewardsPayInCashStored,
    rewardsAmountToPayStored,
    isLoading,
  } = useRewardsSectionConversion();
  const { maxToPayInCash, maxToPayInPoints } = accountPoints;

  const setPayableAmounts = useCallback(
    (payInPoints: number, payInCash?: FiatPrice) => {
      if (payInCash != null) {
        send(setRewardsAmountToPayInCash(payInCash));
      }

      setRewardsPayInPoints(payInPoints);
      send(setRewardsAmountToPay(payInPoints));
    },
    [send]
  );

  useEffect(() => {
    const payInPoints = rewardsPayInCashData?.numberOfPoints ?? 0;
    const payInCash = rewardsPayInCashData?.cashAmount ?? ZERO_FIAT_AMOUNT;

    setPayableAmounts(payInPoints, payInCash);
  }, [rewardsPayInCashData, setPayableAmounts, maxToPayInCash]);

  // Set initial value to max points in input based on cost of flight and points in account
  useEffect(() => {
    if (isRewardsSelected) {
      if (
        rewardsAmountToPayStored > maxToPayInPoints ||
        rewardsAmountToPayStored < 1
      ) {
        setPayableAmounts(maxToPayInPoints, maxToPayInCash);
      }
    } else {
      send(setRewardsAmountToPay(0));
      send(setRewardsAmountToPayInCash({ ...maxToPayInCash, value: 0 }));
    }
  }, [isRewardsSelected, maxToPayInPoints]);

  return {
    context: {
      ...accountPoints,
      rewardsSelected: isRewardsSelected,
      maxToPayInPoints,
      maxToPayInCash,
      rewardsPayInPoints,
      rewardsPayInCash: rewardsPayInCashStored,
      isLoading,
    },
    handlers: {
      onChange: (points) =>
        setRewardsPayInPoints(Math.min(parsePoint(points), maxToPayInPoints)),
      onValueCommit: (points: string | number) => {
        // set points payment only, cash payment will be set in useEffect after the conversion API responds
        setPayableAmounts(parsePoint(points));
      },
    },
  };
};

export const useRewardsSectionConversion = () => {
  const accountPoints = useAccountPoints();
  const { isToPointLoading, isBalanceLoading } = accountPoints;
  const rewardsAmountToPayStored = useCheckoutStateSelector(
    getRewardsAmountToApply
  );
  const rewardsPayInCashStored = useCheckoutStateSelector(
    getRewardsAmountToApplyInCash
  );

  const { data: rewardsPayInCashData, isFetching: isToCashLoading } =
    useConvertPointsToCashQuery(rewardsAmountToPayStored);

  return {
    accountPoints,
    rewardsPayInCashData,
    rewardsAmountToPayStored,
    rewardsPayInCashStored,
    isLoading: isBalanceLoading || isToPointLoading || isToCashLoading,
  };
};
